/**
 * 设备检测和环境配置插件
 *
 * 整合了移动端检测、meta 标签配置、PURE_HOST 检测以及操作系统类名添加功能
 * 支持服务端渲染（SSR）
 */

import { message } from 'ant-design-vue';
import type { NuxtAppConfig } from 'nuxt/schema';
import { PURE_HOST } from '~/utils/constant';

/**
 * 从 User-Agent 检测操作系统类型
 */
function detectOSFromUA(userAgent: string): string {
  const ua = userAgent.toLowerCase();
  const platform = userAgent.toLowerCase();

  if (
    platform.includes('mac') ||
    ua.includes('macintosh') ||
    ua.includes('iphone') ||
    ua.includes('ipad')
  ) {
    return 'macos';
  } else if (platform.includes('win') || ua.includes('windows')) {
    return 'windows';
  } else {
    return 'other';
  }
}

export default defineNuxtPlugin(() => {
  if (import.meta.client) {
    message.config({ top: '72px' });
  }

  const headConfig: NuxtAppConfig['head'] = {
    meta: [],
    script: [],
  };

  // 1. 设备检测（支持 SSR）
  const { isMobile } = useDevice();

  // 在服务端也通过 User-Agent 进行检测，确保 SSR 时也能正确识别
  let detectedIsMobile = isMobile;
  if (import.meta.server) {
    const headers = useRequestHeaders();
    const userAgent = headers['user-agent'] || '';
    // 移动端 User-Agent 检测
    const mobileRegex = /Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    detectedIsMobile = mobileRegex.test(userAgent);
  }

  // 2. 移动端 meta 标签配置
  if (detectedIsMobile) {
    headConfig.meta = [
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no',
      },
      {
        name: 'applicable-device',
        content: 'mobile',
      },
      {
        name: 'force-rendering',
        content: 'webkit',
      },
      {
        name: 'apple-mobile-web-app-capable',
        content: 'yes',
      },
      {
        name: 'full-screen',
        content: 'true',
      },
      {
        name: 'screen-orientation',
        content: 'portrait',
      },
      {
        name: 'x5-fullscreen',
        content: 'true',
      },
      {
        name: '360-fullscreen',
        content: 'true',
      },
      {
        name: 'apple-mobile-web-app-status-bar-style',
        content: 'black',
      },
      {
        name: 'format-detection',
        content: 'telephone=no',
      },
    ];
  }

  useHead(headConfig, { tagPriority: 'critical' });

  // 3. PURE_HOST 检测（支持 SSR）
  const url = useRequestURL();
  const isPure =
    PURE_HOST.some(item => item.test(url.host)) || url.searchParams.get('pure') === '1';

  // 4. 操作系统类名添加（支持 SSR）
  if (import.meta.server) {
    // 服务端：从 User-Agent 检测操作系统并直接设置 class
    const headers = useRequestHeaders();
    const userAgent = headers['user-agent'] || '';
    const detectedOS = detectOSFromUA(userAgent);

    if (detectedOS) {
      useHead({
        htmlAttrs: {
          class: `is-${detectedOS}`,
        },
        bodyAttrs: {
          class: `is-${detectedOS}`,
        },
      });
    }
  } else {
    // 客户端：仅在服务端未设置时进行降级检测
    const addOSClass = () => {
      // 检查服务端是否已经设置了 is-* class
      const htmlClasses = Array.from(document.documentElement.classList);
      const bodyClasses = Array.from(document.body.classList);
      const hasOSClass =
        htmlClasses.some(cls => cls.startsWith('is-')) ||
        bodyClasses.some(cls => cls.startsWith('is-'));

      if (hasOSClass) {
        // 服务端已经设置，无需处理
        return;
      }

      // 降级方案：使用客户端 API 检测
      let os = '';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const nav = navigator as any;
      if (nav.userAgentData) {
        nav.userAgentData
          .getHighEntropyValues(['platform'])
          .then((ua: { platform: string }) => {
            os = ua.platform.toLowerCase();
            applyClass(os);
          })
          .catch(() => fallback());
      } else {
        fallback();
      }

      function fallback() {
        const platform = navigator.platform.toLowerCase();
        const ua = navigator.userAgent.toLowerCase();

        if (platform.includes('mac') || ua.includes('macintosh')) {
          os = 'macos';
        } else if (platform.includes('win') || ua.includes('windows')) {
          os = 'windows';
        } else {
          os = 'other';
        }
        applyClass(os);
      }

      function applyClass(os: string) {
        document.documentElement.classList.add(`is-${os}`);
        document.body.classList.add(`is-${os}`);
      }
    };

    // 等 DOM 渲染完成再加 class
    if (document.readyState === 'loading') {
      // 使用 once 确保事件只触发一次，自动清理
      window.addEventListener('DOMContentLoaded', addOSClass, { once: true });
    } else {
      addOSClass();
    }
  }

  const runtimeConfig = useRuntimeConfig();

  return {
    provide: {
      isPure: isPure || runtimeConfig.public.privatization,
    },
  };
});
